// client/src/reservations/components/TimeResultsGrid.jsx
import React, { useMemo } from 'react';
import OfferingCard from './OfferingCard';
import { psSlotsLocal, cbSessionsLocal, acAvailabilityLocal } from '../utils/availabilityLocal';
import { msSlotsClient } from '../utils/msClient';

// Helper: check if date (YYYY-MM-DD) lies within [start, start+windowDays-1]
function isWithinWindow(dateISO, startISO, windowDays) {
  if (!dateISO || !startISO || !windowDays) return false;
  const d = new Date(dateISO);
  const start = new Date(startISO);
  const end = new Date(start);
  end.setDate(end.getDate() + Math.max(1, Number(windowDays)) - 1);
  return d >= start && d <= end;
}

// Check if a bundle can accommodate the party size within the time window
function checkBundleAvailabilityForPartyAndTimeWindow(bundle, partyNeed, dayList, fromHH, toHH, context = {}) {
  if (!bundle.items?.length) return false;
  
  const windowDays = Math.max(1, Number(bundle.consumption_window_days || 2));
  const { 
    offeringsIndex = {}, 
    employeesIndex = {}, 
    hoursCtx, 
    psSchedule, 
    psEmpWork, 
    psOffDays, 
    psScheduleMode,
    psActivePeriods,
    psEquipPools,
    psEquipUsage,
    cbSessions,
    acSeasons,
    msRules,
    live,
    timezone,
    firstBookableTs
  } = context;

  // Helper to check time window constraints
  const timeWithin = (t) => {
    if (!t) return false;
    if (fromHH && t < fromHH) return false;
    if (toHH && t > toHH) return false;
    return true;
  };

  // Helper: does an item have at least one bookable time inside [s, s+windowDays-1]?
  function itemOKWithinWindow(bundleItem, startISO) {
    // Find the actual offering object for this bundle item
    let offering = null;
    if (bundleItem.mode === 'personal_services') {
      offering = Object.values(offeringsIndex).find(o => o.kind === 'ps_service' && String(o.id) === String(bundleItem.item_id));
    } else if (bundleItem.mode === 'class_based') {
      offering = Object.values(offeringsIndex).find(o => o.kind === 'cb_class' && String(o.id) === String(bundleItem.item_id));
    } else if (bundleItem.mode === 'modular_seating') {
      offering = Object.values(offeringsIndex).find(o => o.kind === 'ms_table_type' && (String(o.id) === String(bundleItem.item_id) || String(o.ms_seating_config_id) === String(bundleItem.item_id)));
    } else if (bundleItem.mode === 'accommodation') {
      offering = Object.values(offeringsIndex).find(o => o.kind === 'ac_room_type' && String(o.id) === String(bundleItem.item_id));
    }

    if (!offering) return false;

    // Check availability for each day in the window
    for (let i = 0; i < windowDays; i++) {
      const d = new Date(startISO);
      d.setDate(d.getDate() + i);
      const dayISO = d.toISOString().slice(0,10);

      if (offering.kind === 'ps_service') {
        const employees = offering._employees || employeesIndex[offering.id] || [];
        const r = psSlotsLocal({
          dateISO: dayISO,
          serviceId: offering.id,
          durationMin: offering.duration_min,
          hoursCtx,
          psSchedule,
          empWork: psEmpWork,
          empOff: psOffDays,
          employees,
          tz: timezone,
          firstBookableISO: firstBookableTs,
          live,
          scheduleModeByService: psScheduleMode,
          party: partyNeed,
          activePeriods: psActivePeriods,
          equipPools: psEquipPools,
          equipUsage: psEquipUsage,
        });
        // Check if there are enough simultaneous slots for the party
        let hasAvailability = false;
        const counts = new Map();
        (r?.available || []).forEach(g => (g.slots || []).forEach(s => {
          if (timeWithin(s.start) && timeWithin(s.end)) counts.set(s.start, (counts.get(s.start) || 0) + 1);
        }));
        hasAvailability = Array.from(counts.values()).some(cnt => cnt >= Math.max(1, partyNeed));
        if (hasAvailability) return true;
      } else if (offering.kind === 'cb_class') {
        const r = cbSessionsLocal({ 
          dateISO: dayISO, 
          classId: offering.id, 
          capacity: offering.capacity, 
          cbSessions, 
          tz: timezone, 
          firstBookableISO: firstBookableTs, 
          live 
        });
        const hasAvailability = (r?.sessions || []).some(s => 
          timeWithin(s.start_time) && Number(s.remaining || 0) >= Math.max(1, partyNeed)
        );
        if (hasAvailability) return true;
      } else if (offering.kind === 'ms_table_type') {
        const slots = msSlotsClient({ 
          dateISO: dayISO, 
          party: partyNeed, 
          seatingConfigId: offering.ms_seating_config_id, 
          areaType: offering.ms_area_type, 
          hoursCtx, 
          msRules, 
          live, 
          firstBookableISO: firstBookableTs, 
          tz: timezone 
        });
        const hasAvailability = (slots || []).some(s => timeWithin(s.start) && timeWithin(s.end));
        if (hasAvailability) return true;
      } else if (offering.kind === 'ac_room_type') {
        // AC: require at least one night overlapping within the window
        const d2 = new Date(dayISO); d2.setDate(d2.getDate() + 1);
        const nextISO = d2.toISOString().slice(0,10);
        const r = acAvailabilityLocal({ 
          roomTypeId: offering.id, 
          startDate: dayISO, 
          endDate: nextISO, 
          quantity: offering.quantity, 
          seasons: acSeasons, 
          live 
        });
        const capOK = !Number(offering.max_guests) || Number(offering.max_guests) >= Math.max(1, partyNeed);
        if (capOK && (r?.available || 0) > 0) return true;
      }
    }
    return false;
  }

  // Check if any date in our search range can serve as a valid bundle start
  for (const startDate of dayList) {
    let allItemsOK = true;
    for (const bundleItem of bundle.items) {
      if (!itemOKWithinWindow(bundleItem, startDate)) {
        allItemsOK = false;
        break;
      }
    }
    if (allItemsOK) return true;
  }

  return false;
}

const within = (t, from, to) => {
  if (!t) return false;
  if (from && t < from) return false;
  if (to && t > to) return false;
  return true;
};

function daysBetween(fromISO, toISO) {
  if (!fromISO && !toISO) return [];
  const start = new Date(fromISO || toISO);
  const end = new Date(toISO || fromISO || fromISO);
  const out = [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    out.push(d.toISOString().slice(0,10));
  }
  return out;
}

export default function TimeResultsGrid({
  offerings,
  filters,                 // { day_from, day_to, win_start, win_end, party, category, ac_exact }
  bookingRange,
  timezone,
  firstBookableTs,
  hoursCtx,
  psSchedule,
  psEmpWork,
  psOffDays,
  psScheduleMode,
  psActivePeriods=[],
  psEquipPools=[],
  psEquipUsage=[],
  cbSessions=[],
  acSeasons=[],
  acModifiers=[],
  msRules,
  live,
  feesCtx,
  editCtx,
  currency='EUR',
  employeeIndex={},
  preorder,
  setPreorder,
  onPick
}) {
  const offeringsIndex = React.useMemo(() => Object.fromEntries((offerings||[]).map(o => [o.id, o])), [offerings]);
  const partyNeed = Math.max(1, Number(filters?.party || 1));
  const fromHH = filters?.win_start || '';
  const toHH   = filters?.win_end   || '';
  const dayList = useMemo(
    () => (filters?.day_from || filters?.day_to) ? daysBetween(filters.day_from, filters.day_to) : [],
    [filters?.day_from, filters?.day_to]
  );

  // category map
  const catOk = (o) => {
    const c = filters?.category || '';
    if (!c) return true;
    if (c === 'services') return o.kind === 'ps_service';
    if (c === 'classes')  return o.kind === 'cb_class';
    if (c === 'tables')   return o.kind === 'ms_table_type';
    if (c === 'rooms')    return o.kind === 'ac_room_type';
    if (c === 'bundles')  return o.kind === 'bundle';
    return true;
  };

  function hasAvailability(o) {
    if (!catOk(o)) return false;
    if (!dayList.length) return false; // require a date selection for Pick Time

    if (o.kind === 'ps_service') {
      for (const date of dayList) {
        const r = psSlotsLocal({
          dateISO: date,
          serviceId: o.id,
          durationMin: o.duration_min,
          hoursCtx,
          psSchedule,
          employees: o._employees || [],
          empWork: psEmpWork,
          empOff: psOffDays,
          scheduleModeByService: psScheduleMode,
          tz: timezone,
          firstBookableISO: firstBookableTs,
          live,
          party: partyNeed,
          activePeriods: psActivePeriods,
          equipPools: psEquipPools,
          equipUsage: psEquipUsage,
        });
        // count starts across employees within time window
        const counts = new Map();
        (r.available || []).forEach(g => {
          (g.slots || []).forEach(s => {
            if (within(s.start, fromHH, toHH) && within(s.end, fromHH, toHH)) {
              counts.set(s.start, (counts.get(s.start)||0) + 1);
            }
          });
        });
        for (const v of counts.values()) if (v >= partyNeed) return true;
      }
      return false;
    }

    if (o.kind === 'cb_class') {
      for (const date of dayList) {
        const r = cbSessionsLocal({
          dateISO: date,
          classId: o.id,
          capacity: o.capacity,
          cbSessions,
          tz: timezone,
          firstBookableISO: firstBookableTs,
          live
        });
        if ((r.sessions || []).some(s =>
          s.remaining >= partyNeed &&
          within(s.start_time, fromHH, toHH))) return true;
      }
      return false;
    }

    if (o.kind === 'ms_table_type') {
      for (const date of dayList) {
        const slots = msSlotsClient({
          dateISO: date,
          party: partyNeed,
          seatingConfigId: o.ms_seating_config_id,
          areaType: o.ms_area_type,
          hoursCtx,
          msRules,
          live,
          firstBookableISO: firstBookableTs,
          tz: timezone
        });
        if (slots.some(s => within(s?.start, fromHH, toHH) && within(s?.end, fromHH, toHH))) return true;
      }
      return false;
    }

    if (o.kind === 'ac_room_type') {
      // exact dates? require day_from & day_to
      if (filters?.ac_exact) {
        if (!filters?.day_from || !filters?.day_to) return false;
        const r = acAvailabilityLocal({
          roomTypeId: o.id,
          startDate: filters.day_from,
          endDate: filters.day_to,
          quantity: o.quantity,
          seasons: acSeasons,
          live
        });
        return Number(r.available || 0) > 0;
      }
      // flexible: any (start,end) within the chosen window with at least 1 night
      const days = dayList;
      for (let i = 0; i < days.length - 1; i++) {
        for (let j = i + 1; j < days.length; j++) {
          const start = days[i], end = days[j];
          const r = acAvailabilityLocal({
            roomTypeId: o.id,
            startDate: start,
            endDate: end,
            quantity: o.quantity,
            seasons: acSeasons,
            live
          });
          if (Number(r.available || 0) > 0) return true;
        }
      }
      return false;
    }

    if (o.kind === 'bundle') {
      // For bundles, we need to check if they can accommodate the current party size within the search window
      return checkBundleAvailabilityForPartyAndTimeWindow(o, partyNeed, dayList, fromHH, toHH, {
        offeringsIndex,
        employeesIndex: employeeIndex,
        hoursCtx,
        psSchedule,
        psEmpWork,
        psOffDays,
        psScheduleMode,
        psActivePeriods,
        psEquipPools,
        psEquipUsage,
        cbSessions,
        acSeasons,
        msRules,
        live,
        timezone,
        firstBookableTs
      });
    }

    return false;
  }

  const visible = useMemo(
    () => offerings.filter(hasAvailability),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      offerings,
      partyNeed,
      fromHH, toHH,
      timezone,
      firstBookableTs,
      JSON.stringify(hoursCtx),
      JSON.stringify(psSchedule),
      JSON.stringify(psEmpWork),
      JSON.stringify(psOffDays),
      JSON.stringify(psScheduleMode),
      JSON.stringify(psActivePeriods),
      JSON.stringify(psEquipPools),
      JSON.stringify(psEquipUsage),
      JSON.stringify(cbSessions),
      JSON.stringify(acSeasons),
      JSON.stringify(msRules),
      JSON.stringify(live),
      JSON.stringify(employeeIndex),
      filters?.day_from, filters?.day_to, filters?.category, filters?.ac_exact
    ]
  );

  if (!dayList.length) {
    return <div className="text-sm text-gray-500">Pick a date (or range) above and press Apply.</div>;
  }

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
      {visible.map(o => (
        <OfferingCard
          key={`${o.kind}-${o.id}`}
          offering={o}
          onPick={onPick}
          bookingRange={bookingRange}
          cbSessions={cbSessions}
          acSeasons={acSeasons}
          acModifiers={acModifiers}
          timezone={timezone}
          firstBookableTs={firstBookableTs}
          hoursCtx={hoursCtx}
          live={live}
          msRules={msRules}
          employeesIndex={employeeIndex}
          psSchedule={psSchedule}
          psEmpWork={psEmpWork}
          psOffDays={psOffDays}
          psScheduleMode={psScheduleMode}
          currency={currency}
          feesCtx={feesCtx}
          editCtx={editCtx}
          preorder={preorder}
          setPreorder={setPreorder}
          offeringsIndex={offeringsIndex}
          // NEW: pre-filter inline pickers to the selected window + give a default date
          winStart={fromHH}
          winEnd={toHH}
          defaultDate={filters?.day_from || ''}
        />
      ))}
      {!visible.length && (
        <div className="text-sm text-amber-700">No offerings match your filters and time window.</div>
      )}
    </div>
  );
}
